import { useMutation } from 'react-query';
import { Lists } from '~/api/scheme';
import useWarnings from './useWarnings';
import useCreditValidator from '~/hooks/useCreditValidator';
import useCurrentUser from '~/hooks/useCurrentUser';
import useListDialogs from '~/hooks/useListDialogs';
import { SelectedRowsProps } from './table/useSelectedRows';
import { OpportunityJSONAPI } from '~/api/resources/opportunity';

export type ToggleSyncedCallback = {
  list_id: string;
  synced: boolean;
  syncSuccess: boolean;
  updated: {
    updated_at: string;
    updated_by: string;
  };
};

type Args = {
  listId: string;
  isSynced: boolean;
  selectedIds?: string[];
  listType?: string;
  successToggleSyncCallback: (params: ToggleSyncedCallback) => void;
  selectedRows?: OpportunityJSONAPI[];
};

type Result = {
  toggleSynced: () => void;
  toggleSyncedMutate: () => void;
};

const useSyncList = (args: Args): Result => {
  const { listId, isSynced, listType, successToggleSyncCallback, selectedIds, selectedRows } = args;
  const { changeWarningsState } = useWarnings();
  const { validateCredits } = useCreditValidator();
  const { currentUser } = useCurrentUser();
  const { closeReviewCreditsDialog } = useListDialogs();

  const mutation = useMutation((params: Lists.ToggleSyncedParams) => Lists.toggleSynced(params), {
    onSuccess(response, params) {
      const {
        data: { sync_success, updated },
      } = response;

      if (params.synced && sync_success !== null && !sync_success) {
        changeWarningsState('syncList', true);
      }

      successToggleSyncCallback({
        ...params,
        ...{
          syncSuccess: sync_success,
          updated: updated,
        },
      });
    },
    onError() {
      changeWarningsState('syncList', true);
    },
  });

  const toggleSynced = () => {
    const itemsToSync = selectedRows?.filter((row) => !row.attributes.synced_with_bpd_crm);
    if (listType === 'OpportunityList' && isSynced && itemsToSync && itemsToSync.length > 0) {
      validateCredits(
        'toggle',
        listId,
        selectedRows || [],
        currentUser?.attributes.credits_amount || 0,
        1
      );
    } else {
      toggleSyncedMutate();
      closeReviewCreditsDialog(true);
    }
  };

  const toggleSyncedMutate = () => {
    mutation.mutate({
      list_id: listId,
      synced: isSynced,
    });
  };

  return {
    toggleSynced,
    toggleSyncedMutate,
  };
};

export default useSyncList;
